import React, { useCallback, useEffect, useState } from "react";
import style from "./OrganizerPage.module.scss";
import Avatar from "./Images/Avatar card.svg";
import teleg from "./Images/telegram.svg";
import odnaclass from "./Images/Odnaclas.svg";
import vk from "./Images/vk.svg";
import { Types } from "types/types";
import { fetchEvents } from "components/ApiService/a-api.service";
import { Link, useNavigate, useParams } from "react-router-dom";
import Loader from "components/Loader/Loader";
import { OrganizerModal } from "./OrganizerModalePage";
import { getAuthToken } from "../../utils/utils";
import ModalNoAuth from "../../components/ui/ModalNoAuth/ModalNoAuth";
import MovieCard from "../../components/MovieList/MovieCard/MovieCard";
import styles from "../../components/MovieList/MovieList/MovieList.module.scss";
import longArrowRight from "../../icons/longArrowRight.svg";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useWidth } from "../../hooks/useWidth";
import { API_BASE } from "../../config/api";

interface Organizer {
  avatar?: {
    medium: string | null;
  };
  first_name?: string;
  description?: string;
}

export const OrganizerPage: React.FC = () => {
  //-------------- Выбранные события, категории и пользователи --------------------
  const [activeFilter, setActiveFilter] = useState<string>("all");
  const [filteredEvents, setFilteredEvents] = useState<Types[]>([]);
  const [organizer, setOrganizer] = useState<Organizer | null>(null);
  //---------------Ожидаемые и завершенные события----------------------------------
  const [upcomingEvents, setUpcomingEvents] = useState<Types[]>([]);
  const [finishedEvents, setFinishedEvents] = useState<Types[]>([]);
  //--------------Состояние кнопки Смотреть больше и сколько событий изначально отображать--------
  const [initialLoad, setInitialLoad] = useState<boolean>(true);
  const [limit, setLimit] = useState<number>(3);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [token, setToken] = useState<string | undefined>();

  const isMobile = useIsMobile();
  const width = useWidth();
  const navigate = useNavigate();
  const { id } = useParams();

  // Для модального окна Авторизации
  const handleOverlayClick = useCallback(() => {
    navigate("/"); // Перенаправляем на главную страницу
  }, [navigate]);

  const handleFilterClick = (filter: string) => {
    setActiveFilter(filter);
  };

  const handleReportClick = () => {
    setIsModalOpen(true);
  };

  // ------------------ Отправка жалобы на организатора ------------------
  const handleSendReport = async (payload: {
    reason: "fraud" | "insult_and_rudeness" | "other";
    message: string;
  }) => {
    try {
      const authToken = getAuthToken();
      if (!authToken) throw new Error("Вы не авторизованы");

      const res = await fetch(`${API_BASE}/complaints/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          user_to: Number(id),
          type_complaint: payload.reason,
          description: payload.message,
        }),
      });

      if (!res.ok) throw new Error("Ошибка отправки жалобы");

      alert("Жалоба отправлена");
      setIsModalOpen(false);
    } catch (err) {
      console.error(err);
      alert("Не удалось отправить жалобу. Попробуйте позже.");
    }
  };

  // Загрузка данных организатора
  const fetchOrganizerData = useCallback(
    async (token: string) => {
      try {
        const response = await fetch(`${API_BASE}/users/profile/${id}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setOrganizer(data);
        setLoading(false);
      } catch (err) {
        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError("An unknown error occurred");
        }
        setLoading(false);
      }
    },
    [id]
  );

  // Получение токена
  useEffect(() => {
    const storedToken = getAuthToken();
    const localId = localStorage.getItem("userId");
    setToken(storedToken);

    if (!storedToken) {
      setLoading(false);
    }

    if (localId === id) {
      navigate("/accountPage");
    }
  }, [id, navigate]);

  useEffect(() => {
    if (token) {
      fetchOrganizerData(token);
    }
  }, [token, fetchOrganizerData]);

  // Загрузка событий
  useEffect(() => {
    const fetchData = async () => {
      try {
        const dataCurrent = await fetchEvents("current_or_planned", Number(id));
        const dataFinished = await fetchEvents("finished", Number(id));

        setUpcomingEvents(dataCurrent);
        setFinishedEvents(dataFinished);
        setFilteredEvents([...dataCurrent, ...dataFinished]);
      } catch (error) {
        console.error("Ошибка загрузки событий:", error);
      }
    };

    fetchData();
  }, [id]);

  useEffect(() => {
    if (activeFilter === "all") {
      setFilteredEvents([...upcomingEvents, ...finishedEvents]);
    } else if (activeFilter === "completed") {
      setFilteredEvents(finishedEvents);
    } else if (activeFilter === "upcoming") {
      setFilteredEvents(upcomingEvents);
    }
  }, [activeFilter, upcomingEvents, finishedEvents]);

  useEffect(() => {
    if (width <= 1430) {
      setLimit(2);
    } else setLimit(3);

    if (!initialLoad || isMobile) {
      setLimit(filteredEvents.length);
    }
  }, [width, initialLoad, filteredEvents, isMobile]);

  if (loading)
    return (
      <div>
        <Loader />
      </div>
    );
  if (error) return <div>Error: {error}</div>;

  if (!token) {
    return (
      <div className="modal-overlay" onClick={handleOverlayClick}>
        <div className="modal" onClick={(e) => e.stopPropagation()}>
          <ModalNoAuth />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={style.organizerPage}>
        <div className={style.mainInfo}>
          <div className={style.leftColumn}>
            <div className={style.profilePicture}>
              <img
                src={organizer?.avatar?.medium || Avatar}
                alt="Profile"
                className={style.obscuredFace}
              />
              <h2 className={style.NameOrgSmallScreen}>
                {organizer?.first_name}
              </h2>
            </div>
            <div className={style.IconAction}>
              <div className={style.iconSocial}>
                <img src={teleg} alt="telegram" />
                <img src={vk} alt="vk" />
                <img src={odnaclass} alt="odnaclass" />
              </div>
              <button
                className={style.reportButton}
                onClick={handleReportClick}
              >
                Пожаловаться
              </button>
              <button className={style.subscribeButton}>Подписаться</button>
            </div>
          </div>
          <div className={style.rightColumn}>
            <div className={style.titleAndName}>
              <h1 className={style.pageTitle}>Страница организатора</h1>
              <h2 className={style.organizerName}>{organizer?.first_name}</h2>
            </div>
            <div className={style.description}>
              <p>{organizer?.description}</p>
            </div>
          </div>
        </div>

        <div className={style.eventsSection}>
          <h3 className={style.eventsTitle}>События организатора</h3>
          <div className={style.filters}>
            <button
              className={`${style.filterButton} ${
                activeFilter === "all" ? style.active : ""
              }`}
              style={isMobile ? {} : { width: "61px" }}
              onClick={() => handleFilterClick("all")}
            >
              Все
            </button>
            <button
              className={`${style.filterButton} ${
                activeFilter === "completed" ? style.active : ""
              }`}
              style={isMobile ? {} : { width: "148px" }}
              onClick={() => handleFilterClick("completed")}
            >
              Завершённые
            </button>
            <button
              className={`${style.filterButton} ${
                activeFilter === "upcoming" ? style.active : ""
              }`}
              style={isMobile ? {} : { width: "125px" }}
              onClick={() => handleFilterClick("upcoming")}
            >
              Ожидается
            </button>
          </div>
        </div>

        <div className={style.cardsGrid}>
          {filteredEvents.length > 0 ? (
            filteredEvents.slice(0, limit).map((event) => (
              <Link
                to={`/event/${event.id}`}
                className={`${style.cardLink} ${
                  event.is_finished ? style.disabled : ""
                }`}
                key={event.id}
              >
                <MovieCard event={event} />
              </Link>
            ))
          ) : (
            <p
              style={{
                textAlign: "center",
                fontSize: "18px",
                fontWeight: "bold",
                marginTop: "40px",
              }}
            >
              Событий пока нет
            </p>
          )}
        </div>

        {!isMobile && initialLoad && filteredEvents.length > limit && (
          <div className={styles.seeMore}>
            <button
              className={styles.seeMoreBtn}
              onClick={() => setInitialLoad(false)}
            >
              <span>Смотреть больше</span>
              <img src={longArrowRight} alt="Стрелка вправо" />
            </button>
          </div>
        )}
      </div>

      {/* Модальное окно "Пожаловаться" */}
      {isModalOpen && (
        <OrganizerModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          isUserLoggedIn={!!token}
          onSendReport={handleSendReport}
        />
      )}
    </>
  );
};
